import { db } from "../../../../../firebaseConfig";
import { auth } from "../../../../../firebaseConfig";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";

/**
 * Save project to Firebase
 * @param {Object} project - Project data
 * @returns {Promise<Object>} - Saved project
 */
export const saveProjectToFirebase = async (project) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("User must be authenticated to create a project");
    }

    const projectsRef = collection(db, "projects");

    const teamMembers = Array.isArray(project.teamMembers)
      ? project.teamMembers
      : [];
    const teamMemberIds = teamMembers.map((m) => m.id).filter(Boolean);

    const sections = Array.isArray(project.sections)
      ? project.sections.map((s) => ({
          id: s.id || null,
          title: s.title || "",
          body: s.body || "",
        }))
      : [];
    const phases = Array.isArray(project.phases)
      ? project.phases.map((p) => ({
          id: p.id || null,
          objective: p.objective || "",
          tasks: Array.isArray(p.tasks)
            ? p.tasks.map((t) => ({
                id: t.id || null,
                title: t.title || "",
                content: t.content || "",
              }))
            : [],
        }))
      : [];

    const projectData = {
      postId: project.postId || null,
      creatorId: currentUser.uid,
      title: project.title || project.name || "",
      issue: project.issue || "",
      scale: project.scale || "",
      location: project.location || "",
      imageUrl: project.imageUrl || "",
      mission: project.mission || "",
      solutionType: project.solutionType || "",
      sections,
      phases,
      teamMemberIds,
      teamMembers,
      status: project.status || "active",
      likeCount: 0,
      followerCount: 0,
      shareCount: 0,
      updatesCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(projectsRef, projectData);
    const createdSnap = await getDoc(docRef);
    const createdData = createdSnap.data();

    return {
      id: docRef.id,
      ...createdData,
      createdAt: createdData.createdAt?.toDate?.() || new Date(),
      updatedAt: createdData.updatedAt?.toDate?.() || new Date(),
    };
  } catch (error) {
    console.error("Error saving project:", error);
    throw error;
  }
};

/**
 * Update project fields
 * @param {string} projectId - Project ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} - Updated project
 */
export const updateProjectFields = async (projectId, updates = {}) => {
  try {
    if (!auth?.currentUser) throw new Error("No authenticated user");
    if (!projectId) throw new Error("Missing projectId");
    const projectRef = doc(db, "projects", projectId);
    await updateDoc(projectRef, { ...updates, updatedAt: serverTimestamp() });
    const snap = await getDoc(projectRef);
    return { id: snap.id, ...snap.data() };
  } catch (e) {
    console.error("Error updating project fields:", e);
    throw e;
  }
};

/**
 * Update project status
 * @param {string} projectId - Project ID
 * @param {string} status - New status
 * @returns {Promise<Object>} - Updated project
 */
export const updateProjectStatus = async (projectId, status) => {
  return updateProjectFields(projectId, { status });
};

/**
 * Progress statuses constant
 */
export const PROGRESS_STATUSES = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  COMPLETED: "completed",
};

/**
 * Subscribe to project progress
 * @param {string} projectId - Project ID
 * @param {string} userId - User ID
 * @param {Function} onChange - Callback function
 * @returns {Function} - Unsubscribe function
 */
export const subscribeProjectProgress = (projectId, userId, onChange) => {
  if (!projectId || !userId || typeof onChange !== "function") return () => {};
  const ref = doc(db, "project_progress", `${projectId}_${userId}`);
  const unsub = onSnapshot(
    ref,
    (snap) => {
      if (snap.exists()) onChange({ id: snap.id, ...snap.data() });
      else onChange(null);
    },
    (e) => {
      console.warn("Progress subscribe error:", e);
      onChange(null);
    },
  );
  return unsub;
};

/**
 * Set phase progress
 * @param {string} projectId - Project ID
 * @param {string} userId - User ID
 * @param {string} phaseId - Phase ID
 * @param {string} status - Status
 */
export const setPhaseProgress = async (projectId, userId, phaseId, status) => {
  if (!projectId || !userId || !phaseId)
    throw new Error("Missing ids for setPhaseProgress");
  const ref = doc(db, "project_progress", `${projectId}_${userId}`);
  await setDoc(
    ref,
    {
      projectId,
      userId,
      phases: { [phaseId]: status },
      updatedAt: new Date().toISOString(),
    },
    { merge: true },
  );
};

/**
 * Set task progress
 * @param {string} projectId - Project ID
 * @param {string} userId - User ID
 * @param {string} phaseId - Phase ID
 * @param {string} taskId - Task ID
 * @param {string} status - Status
 */
export const setTaskProgress = async (
  projectId,
  userId,
  phaseId,
  taskId,
  status,
) => {
  if (!projectId || !userId || !phaseId || !taskId)
    throw new Error("Missing ids for setTaskProgress");
  const ref = doc(db, "project_progress", `${projectId}_${userId}`);
  const key = `${phaseId}:${taskId}`;
  await setDoc(
    ref,
    {
      projectId,
      userId,
      tasks: { [key]: status },
      updatedAt: new Date().toISOString(),
    },
    { merge: true },
  );
};
